import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AlertCircle, Plus, Merge, Search, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { productService } from '@/lib/packages';
import type { Database } from '@/integrations/supabase/types';

interface StockComparison {
  productCode: string;
  productName: string;
  category: string;
  currentQuantity: number;
  uploadedQuantity: number;
  quantityChange: number;
  currentCost: number | null;
  uploadedCost: number | null;
  productId?: string;
  isNew: boolean;
  willBeRemoved?: boolean;
}

type ProductRow = Database['public']['Tables']['products']['Row'];

interface StockItem {
  id: string;
  productCode: string;
  productName: string;
  category: string;
  cost: string;
  quantity: string;
  searchTerm?: string;
  searchTermName?: string;
  searchTermCategory?: string;
  showSearchResults?: boolean;
  showSearchResultsName?: boolean;
  showSearchResultsCategory?: boolean;
  errors?: string[];
  isExisting?: boolean;
}

interface ReviewData {
  unmatchedItems: StockItem[];
  matchedItems: StockItem[];
}

const AnnualInventoryReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId } = useAuth();

  // Get data from navigation state
  const reviewData = location.state as ReviewData | null;

  const [unmatchedItems, setUnmatchedItems] = useState<StockItem[]>(reviewData?.unmatchedItems || []);
  const [matchedItems, setMatchedItems] = useState<StockItem[]>(reviewData?.matchedItems || []);
  const [currentUnmatchedIndex, setCurrentUnmatchedIndex] = useState(0);
  const [productMatchSearch, setProductMatchSearch] = useState('');
  const [productMatchResults, setProductMatchResults] = useState<ProductRow[]>([]);
  const [productMatchLoading, setProductMatchLoading] = useState(false);
  const [selectedMatchProduct, setSelectedMatchProduct] = useState<ProductRow | null>(null);

  // Redirect if no data
  useEffect(() => {
    if (!reviewData || !unmatchedItems.length) {
      toast.error('Gösterilecek veri bulunamadı');
      navigate('/stock-entry');
    }
  }, [reviewData, unmatchedItems.length, navigate]);

  // Search products for matching
  const searchProductsForMatch = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setProductMatchResults([]);
      return;
    }

    setProductMatchLoading(true);
    try {
      const results = await productService.searchProducts(companyId, searchTerm, 20);
      setProductMatchResults(results);
    } catch (error) {
      console.error('Error searching products:', error);
      toast.error('Ürün arama sırasında hata oluştu');
    } finally {
      setProductMatchLoading(false);
    }
  }, [companyId]);

  // Search products when productMatchSearch changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productMatchSearch) {
        searchProductsForMatch(productMatchSearch);
      } else {
        setProductMatchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productMatchSearch, searchProductsForMatch]);

  // Pre-populate search with unmatched item's product code when item changes
  useEffect(() => {
    if (unmatchedItems.length > 0 && currentUnmatchedIndex < unmatchedItems.length) {
      const currentItem = unmatchedItems[currentUnmatchedIndex];
      // Pre-populate with product code to help with matching (only if search is empty)
      if (currentItem.productCode && productMatchSearch === '') {
        const codeToSearch = currentItem.productCode;
        setProductMatchSearch(codeToSearch);
        // Trigger search immediately after a short delay
        setTimeout(() => {
          searchProductsForMatch(codeToSearch);
        }, 100);
      }
    }
  }, [currentUnmatchedIndex, unmatchedItems, productMatchSearch, searchProductsForMatch]);

  // Handle creating new product from unmatched item
  const handleCreateNewProductFromUnmatched = async () => {
    if (currentUnmatchedIndex >= unmatchedItems.length) return;

    const currentItem = unmatchedItems[currentUnmatchedIndex];
    
    // Add to matched items (will be created when saving)
    const newMatched = [...matchedItems, currentItem];
    setMatchedItems(newMatched);

    // Remove from unmatched and move to next
    const newUnmatched = unmatchedItems.filter((_, idx) => idx !== currentUnmatchedIndex);
    setUnmatchedItems(newUnmatched);

    if (newUnmatched.length > 0) {
      // Move to next unmatched item
      setCurrentUnmatchedIndex(0);
      setProductMatchSearch('');
      setProductMatchResults([]);
      setSelectedMatchProduct(null);
    } else {
      // All items processed, proceed to comparison
      handleFinishReview(newMatched);
    }
  };

  // Handle matching with existing product
  const handleMatchWithExistingProduct = async () => {
    if (currentUnmatchedIndex >= unmatchedItems.length || !selectedMatchProduct) return;

    const currentItem = unmatchedItems[currentUnmatchedIndex];
    const matchedProductCode = selectedMatchProduct.item_code || '';

    // Find if this product already exists in matched items
    const existingMatchedIndex = matchedItems.findIndex(
      item => item.productCode.toLowerCase() === matchedProductCode.toLowerCase()
    );

    let updatedMatched: StockItem[];

    if (existingMatchedIndex >= 0) {
      // Merge with existing matched item
      const existingItem = matchedItems[existingMatchedIndex];
      const currentQuantity = parseInt(existingItem.quantity) || 0;
      const newQuantity = parseInt(currentItem.quantity) || 0;
      const mergedQuantity = currentQuantity + newQuantity;

      // Use highest cost
      const existingCost = parseFloat(existingItem.cost) || 0;
      const newCost = parseFloat(currentItem.cost) || 0;
      const highestCost = Math.max(existingCost, newCost);

      updatedMatched = [...matchedItems];
      updatedMatched[existingMatchedIndex] = {
        ...existingItem,
        quantity: mergedQuantity.toString(),
        cost: highestCost.toString()
      };
      setMatchedItems(updatedMatched);
    } else {
      // Add as new matched item with matched product info
      const newMatchedItem: StockItem = {
        ...currentItem,
        productCode: matchedProductCode,
        productName: selectedMatchProduct.name || currentItem.productName,
        category: selectedMatchProduct.category || currentItem.category,
        cost: currentItem.cost || (selectedMatchProduct.current_cost?.toString() || '0')
      };
      updatedMatched = [...matchedItems, newMatchedItem];
      setMatchedItems(updatedMatched);
    }

    // Remove from unmatched and move to next
    const newUnmatched = unmatchedItems.filter((_, idx) => idx !== currentUnmatchedIndex);
    setUnmatchedItems(newUnmatched);

    if (newUnmatched.length > 0) {
      // Move to next unmatched item
      setCurrentUnmatchedIndex(0);
      setProductMatchSearch('');
      setProductMatchResults([]);
      setSelectedMatchProduct(null);
    } else {
      // All items processed, proceed to comparison
      handleFinishReview(updatedMatched);
    }
  };

  // Compare with current stock for annual inventory (SET mode, not ADD)
  const compareWithCurrentStock = async (excelItems: StockItem[]): Promise<StockComparison[]> => {
    if (!companyId) return [];

    try {
      // Fetch all current products with their stock
      const { data: currentProducts, error } = await supabase
        .from('products')
        .select('id, item_code, name, category, quantity_on_hand, current_cost')
        .eq('company_id', companyId);

      if (error) {
        console.error('Error fetching current stock:', error);
        toast.error('Mevcut stok bilgileri alınamadı');
        return [];
      }

      const comparisons: StockComparison[] = [];
      const currentStockMap = new Map<string, {
        id: string;
        name: string;
        category: string | null;
        quantity: number;
        cost: number | null;
      }>();

      // Build map of current stock
      currentProducts?.forEach(product => {
        if (product.item_code) {
          currentStockMap.set(product.item_code.toLowerCase(), {
            id: product.id,
            name: product.name || '',
            category: product.category,
            quantity: product.quantity_on_hand || 0,
            cost: product.current_cost
          });
        }
      });

      // Compare Excel items with current stock - for annual inventory, SET quantity (not add)
      excelItems.forEach(item => {
        const productCode = item.productCode.toLowerCase();
        const currentStock = currentStockMap.get(productCode);
        const uploadedQty = parseInt(item.quantity) || 0;
        const uploadedCost = parseFloat(item.cost) || null;

        if (currentStock) {
          // Existing product - SET quantity to Excel value
          const quantityChange = uploadedQty - currentStock.quantity;

          comparisons.push({
            productCode: item.productCode,
            productName: item.productName || currentStock.name,
            category: item.category || currentStock.category || '',
            currentQuantity: currentStock.quantity,
            uploadedQuantity: uploadedQty, // Excel quantity (will be SET, not added)
            quantityChange: quantityChange,
            currentCost: currentStock.cost,
            uploadedCost: uploadedCost,
            productId: currentStock.id,
            isNew: false
          });
        } else {
          // New product
          comparisons.push({
            productCode: item.productCode,
            productName: item.productName,
            category: item.category,
            currentQuantity: 0,
            uploadedQuantity: uploadedQty,
            quantityChange: uploadedQty,
            currentCost: null,
            uploadedCost: uploadedCost,
            isNew: true
          });
        }
      });

      return comparisons;
    } catch (error) {
      console.error('Error comparing stock:', error);
      toast.error('Stok karşılaştırması yapılırken hata oluştu');
      return [];
    }
  };

  // Validate items
  const validateItems = async (items: StockItem[]): Promise<StockItem[]> => {
    if (!companyId) return items;

    try {
      // Get all existing product codes for this company
      const { data: existingProducts } = await supabase
        .from('products')
        .select('item_code, id, name')
        .eq('company_id', companyId);
      
      const existingCodes = new Set(existingProducts?.map(p => p.item_code) || []);
      
      return items.map(item => {
        const errors: string[] = [];
        
        // Basic validation
        if (!item.productCode || item.productCode.trim() === '') {
          errors.push('Ürün kodu gereklidir');
        }
        if (!item.productName || item.productName.trim() === '') {
          errors.push('Ürün adı gereklidir');
        }
        if (item.quantity === '' || item.quantity === null || item.quantity === undefined) {
          errors.push('Miktar gereklidir');
        } else if (isNaN(parseInt(item.quantity)) || parseInt(item.quantity) < 0) {
          errors.push('Geçerli bir miktar giriniz (0 veya pozitif sayı)');
        }
        
        const isExisting = existingCodes.has(item.productCode);
        
        return {
          ...item,
          errors: errors.length > 0 ? errors : undefined,
          isExisting
        };
      });
    } catch (error) {
      console.error('Error validating items:', error);
      return items;
    }
  };

  // Finish review and navigate to comparison page
  const handleFinishReview = async (finalMatchedItems: StockItem[]) => {
    try {
      toast.info('Karşılaştırma hazırlanıyor...');
      
      // Validate all items
      const validatedItems = await validateItems(finalMatchedItems);
      
      // Compare with current stock
      const comparison = await compareWithCurrentStock(validatedItems);
      
      if (comparison.length === 0) {
        toast.error('Karşılaştırma verisi oluşturulamadı');
        return;
      }
      
      // Navigate directly to comparison page
      navigate('/annual-inventory-comparison', {
        state: {
          comparison: comparison,
          excelItems: validatedItems,
          mode: 'update' as const
        }
      });
    } catch (error) {
      console.error('Error finishing review:', error);
      toast.error('Karşılaştırma sayfasına geçilirken hata oluştu');
    }
  };

  // Handle cancel - go back to stock entry
  const handleCancel = () => {
    if (window.confirm('İşlemi iptal etmek istediğinize emin misiniz? Tüm eşleştirmeler kaybolacak.')) {
      navigate('/stock-entry');
    }
  };

  if (!unmatchedItems.length) {
    return null;
  }

  const currentItem = unmatchedItems[currentUnmatchedIndex];

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Geri
            </Button>
            <div>
              <h1 className="text-h1">Yıllık Sayım - Ürün Eşleştirme</h1>
              <p className="text-muted-foreground">
                Eşleşmeyen ürünler için işlem seçin (Kalan: {unmatchedItems.length} ürün)
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>İlerleme</span>
                <span>Kalan: {unmatchedItems.length} ürün</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Unmatched Item */}
        <Card className="border-accent/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-accent" />
              Eşleşmeyen Ürün
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Ürün Kodu</Label>
                <p className="text-base font-medium">{currentItem.productCode}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Ürün Adı</Label>
                <p className="text-base font-medium">{currentItem.productName || '-'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Miktar</Label>
                <p className="text-base font-medium">{currentItem.quantity}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Maliyet</Label>
                <p className="text-base font-medium">₺{parseFloat(currentItem.cost || '0').toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Options */}
        <Card>
          <CardHeader>
            <CardTitle>İşlem Seçin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Option 1: Create New Product */}
            <div>
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-4"
                onClick={handleCreateNewProductFromUnmatched}
              >
                <Plus className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-medium text-base">Yeni Ürün Oluştur</div>
                  <div className="text-sm text-muted-foreground">Bu ürün kodunu yeni ürün olarak ekle</div>
                </div>
              </Button>
            </div>

            {/* Option 2: Match with Existing Product */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Mevcut Ürünle Eşleştir</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Ürün ara (kod veya isim)..."
                  value={productMatchSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProductMatchSearch(value);
                    setSelectedMatchProduct(null);
                    // Trigger search immediately if length >= 2
                    if (value.length >= 2) {
                      searchProductsForMatch(value);
                    } else {
                      setProductMatchResults([]);
                    }
                  }}
                  onFocus={() => {
                    // If there's already text, trigger search
                    if (productMatchSearch.length >= 2) {
                      searchProductsForMatch(productMatchSearch);
                    }
                  }}
                  className="pl-10"
                />
              </div>

              {/* Search Results */}
              {productMatchLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Aranıyor...</span>
                </div>
              )}

              {!productMatchLoading && productMatchSearch.length >= 2 && productMatchResults.length > 0 && (
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {productMatchResults.map((product) => (
                    <div
                      key={product.id}
                      className={`p-3 cursor-pointer hover:bg-accent/50 border-b last:border-b-0 ${
                        selectedMatchProduct?.id === product.id ? 'bg-accent' : ''
                      }`}
                      onClick={() => setSelectedMatchProduct(product)}
                    >
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {product.item_code} • {product.category || 'Kategorisiz'} • ₺{(product.current_cost || product.initial_cost || 0).toLocaleString('tr-TR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!productMatchLoading && productMatchSearch.length >= 2 && productMatchResults.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  Ürün bulunamadı
                </div>
              )}

              <Button
                className="w-full"
                onClick={handleMatchWithExistingProduct}
                disabled={!selectedMatchProduct}
              >
                <Merge className="w-4 h-4 mr-2" />
                Seçili Ürünle Eşleştir
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Özet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Eşleştirilen Ürünler</Label>
                <p className="text-2xl font-bold text-green-600">{matchedItems.length}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Kalan Ürünler</Label>
                <p className="text-2xl font-bold text-orange-600">{unmatchedItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AnnualInventoryReview;

